import { createClient } from '@supabase/supabase-js';

// Access environment variables securely
// Vite exposes env vars on import.meta.env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug logging: Only print booleans, never secrets
console.log("[env] url?", !!supabaseUrl, "anon?", !!supabaseAnonKey);

// Check if configuration is valid
export const isSupabaseConfigured = (): boolean => {
    return !!(supabaseUrl && supabaseAnonKey);
};

// Safe URL join helper - prevents double URL concatenation
export const getSupabaseUrl = (path: string): string => {
    const base = (supabaseUrl || '').trim().replace(/\/+$/, '');
    if (!base) {
        throw new Error('VITE_SUPABASE_URL is not configured');
    }
    // Remove leading slash from path to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return new URL(cleanPath, base).toString();
};

// Global fetch dedupe cache - prevents duplicate GET requests within 2 seconds
// CRITICAL: We cache the Response promise, but ALWAYS clone the response when returning
// from cache to avoid "Body is disturbed or locked" errors when multiple callers read it.
interface FetchCacheEntry {
    promise: Promise<Response>;
    timestamp: number;
}

const fetchCache = new Map<string, FetchCacheEntry>();
const FETCH_DEDUPE_TTL = 2000; // 2 seconds

const dedupedFetch: typeof fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const method = init?.method || 'GET';
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    
    // Only dedupe GET requests
    if (method === 'GET') {
        const cacheKey = `${method}:${url}`;
        const now = Date.now();
        const cached = fetchCache.get(cacheKey);
        
        // If same request exists and is fresh, return a cloned response
        if (cached && (now - cached.timestamp) < FETCH_DEDUPE_TTL) {
            if (import.meta.env.DEV) {
                console.log('[fetch-dedupe] Using cached request:', cacheKey);
            }
            
            // Return a clone from the cached promise
            // The cached promise stores a clone that we clone again for each caller
            return cached.promise.then(response => {
                return response.clone();
            }).catch(error => {
                // If cloning fails or cached promise rejects, remove from cache and re-fetch
                fetchCache.delete(cacheKey);
                return fetch(input, init);
            });
        }
        
        // Create new fetch and cache it
        // CRITICAL: We immediately clone the response and store the clone in the cache
        // The original response goes to the first caller, the clone stays for future callers
        const promise = fetch(input, init).then(response => {
            // Store a clone in the cache for future callers
            // Update the cached entry with the cloned response
            const clonedResponse = response.clone();
            
            // Create a new promise that resolves to the clone (for cache)
            const cachePromise = Promise.resolve(clonedResponse);
            fetchCache.set(cacheKey, { promise: cachePromise, timestamp: now });
            
            // Clean up cache after TTL
            setTimeout(() => {
                fetchCache.delete(cacheKey);
            }, FETCH_DEDUPE_TTL);
            
            // Return the original response to the first caller
            return response;
        }).catch(error => {
            // Remove from cache on error
            fetchCache.delete(cacheKey);
            throw error;
        });
        
        // Temporarily store the original promise (will be replaced with clone after response arrives)
        fetchCache.set(cacheKey, { promise, timestamp: now });
        
        // Clean up old entries periodically
        if (fetchCache.size > 100) {
            for (const [key, entry] of fetchCache.entries()) {
                if ((now - entry.timestamp) >= FETCH_DEDUPE_TTL) {
                    fetchCache.delete(key);
                }
            }
        }
        
        return promise;
    }
    
    // Non-GET requests pass through
    return fetch(input, init);
};

// Create client with deduped fetch to prevent duplicate requests
export const supabase = createClient(
    supabaseUrl || '',
    supabaseAnonKey || '',
    {
        global: {
            fetch: dedupedFetch
        }
    }
);

// =============================================================================
// T1: Safari-friendly JWT extraction helper (DEV ONLY)
// Usage: Open Safari Console -> Run `await window.__dumpJwt()`
// =============================================================================
if (typeof import.meta.env !== 'undefined' && import.meta.env.DEV) {
  (window as any).__dumpJwt = async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('[__dumpJwt] Session Error:', error.message);
        return null;
      }
      
      const token = data?.session?.access_token || null;
      const userId = data?.session?.user?.id || null;
      const expiresAt = data?.session?.expires_at 
        ? new Date(data.session.expires_at * 1000).toISOString() 
        : null;
      
      console.log('=== SUPABASE SESSION INFO ===');
      console.log('User ID:', userId);
      console.log('Expires At:', expiresAt);
      console.log('Access Token:', token);
      console.log('============================');
      
      if (token) {
        try {
          if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(token);
            console.log('✅ JWT copied to clipboard');
          }
        } catch (e) {
          console.log('⚠️ Clipboard copy failed (Safari permissions). Token is printed above.');
        }
      } else {
        console.log('⚠️ No active session. Please log in first.');
      }
      
      return token;
    } catch (e) {
      console.error('[__dumpJwt] Unexpected error:', e);
      return null;
    }
  };
  
  console.log('[Dev] Safari JWT helper loaded. Run `await window.__dumpJwt()` in console to get your token.');
  
  // Also expose anon key helper for testing (DEV ONLY)
  (window as any).__getAnonKey = () => {
    const key = supabaseAnonKey;
    if (key) {
      console.log('✅ Supabase Anon Key:', key);
      console.log('');
      console.log('💡 To use in test script:');
      console.log('  window.testAnonKey = "' + key + '";');
      console.log('  testIntercomJWT();');
      return key;
    } else {
      console.error('❌ Anon key not found in environment');
      return null;
    }
  };
  
  console.log('[Dev] Anon key helper loaded. Run `window.__getAnonKey()` in console to get your anon key.');
}
